import React from 'react';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { ButtonBase, Grid, IconButton, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { Language } from '../../api';
import { DATE_FORMAT } from '../../consts';
import { DatedSessionWeek, getStudioPrimary } from './utils';

type WeekStepperProps = {
  weeks: DatedSessionWeek[];
  activeWeekIndex: number;
  onChange: (index: number) => void;
  selectedCountByWeek: number[];
  language: Language;
};

const STEP_BUTTON_SIZE = 44;
const DOT_TOUCH_WIDTH = 44;
const DOT_TOUCH_HEIGHT = 32;
const DOT_SIZE = 10;
const ACTIVE_DOT_WIDTH = 26;

const formatWeekRange = (from: string, to: string, isHebrew: boolean): string => {
  const fromMoment = moment(from, DATE_FORMAT);
  const toMoment = moment(to, DATE_FORMAT);
  if (fromMoment.isSame(toMoment, 'day')) {
    return toMoment.format(isHebrew ? 'D/M' : 'M/D');
  }
  if (fromMoment.isSame(toMoment, 'month')) {
    return isHebrew
      ? `${fromMoment.format('D')}–${toMoment.format('D/M')}`
      : `${fromMoment.format('M/D')}–${toMoment.format('D')}`;
  }
  return isHebrew
    ? `${fromMoment.format('D/M')}–${toMoment.format('D/M')}`
    : `${fromMoment.format('M/D')}–${toMoment.format('M/D')}`;
};

export const WeekStepper = ({ weeks, activeWeekIndex, onChange, selectedCountByWeek, language }: WeekStepperProps) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const primary = getStudioPrimary(theme);
  const isRtl = i18n.dir() === 'rtl';
  const isHebrew = language === Language.HE;
  const activeWeek = weeks[activeWeekIndex];
  if (!activeWeek) return null;

  const getPicksText = (count: number): string =>
    count > 0 ? t('pass_dated_sessions.weekPicks', { count }) : t('pass_dated_sessions.weekNoPicks');
  const activePicks = selectedCountByWeek[activeWeekIndex] ?? 0;
  const PreviousIcon = isRtl ? ChevronRight : ChevronLeft;
  const NextIcon = isRtl ? ChevronLeft : ChevronRight;

  return (
    <Grid container flexDirection="column" alignItems="center" gap={0.5}>
      <Grid
        container
        alignItems="center"
        justifyContent="space-between"
        flexWrap="nowrap"
        sx={{
          width: '100%',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          p: 0.5,
        }}>
        <IconButton
          aria-label={t('pass_dated_sessions.previousWeek')}
          disabled={activeWeekIndex === 0}
          onClick={() => onChange(activeWeekIndex - 1)}
          sx={{ width: STEP_BUTTON_SIZE, height: STEP_BUTTON_SIZE }}>
          <PreviousIcon />
        </IconButton>
        <Grid container flexDirection="column" alignItems="center" aria-live="polite">
          <Typography component="span" fontSize={18} fontWeight={700} dir="ltr">
            {formatWeekRange(activeWeek.from, activeWeek.to, isHebrew)}
          </Typography>
          <Typography component="span" variant="caption" color="text.secondary">
            {t('pass_dated_sessions.weekOf', { current: activeWeekIndex + 1, total: weeks.length })}
            {' · '}
            <Typography
              component="span"
              variant="caption"
              fontWeight={activePicks > 0 ? 600 : undefined}
              sx={{ color: activePicks > 0 ? primary.main : 'text.secondary' }}>
              {getPicksText(activePicks)}
            </Typography>
          </Typography>
        </Grid>
        <IconButton
          aria-label={t('pass_dated_sessions.nextWeek')}
          disabled={activeWeekIndex === weeks.length - 1}
          onClick={() => onChange(activeWeekIndex + 1)}
          sx={{ width: STEP_BUTTON_SIZE, height: STEP_BUTTON_SIZE }}>
          <NextIcon />
        </IconButton>
      </Grid>
      <Grid container justifyContent="center" role="tablist" flexWrap="nowrap" gap={0.5}>
        {weeks.map((week) => {
          const isActive = week.index === activeWeekIndex;
          const picks = selectedCountByWeek[week.index] ?? 0;
          return (
            <ButtonBase
              key={week.index}
              role="tab"
              aria-selected={isActive}
              aria-label={`${formatWeekRange(week.from, week.to, isHebrew)}, ${getPicksText(picks)}`}
              onClick={() => onChange(week.index)}
              sx={{
                width: DOT_TOUCH_WIDTH,
                height: DOT_TOUCH_HEIGHT,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
              <Grid
                component="span"
                sx={{
                  display: 'block',
                  width: isActive ? ACTIVE_DOT_WIDTH : DOT_SIZE,
                  height: DOT_SIZE,
                  borderRadius: DOT_SIZE / 2,
                  boxSizing: 'border-box',
                  backgroundColor: isActive ? 'text.primary' : picks > 0 ? primary.main : 'background.paper',
                  border: isActive || picks > 0 ? 'none' : '2px solid',
                  borderColor: 'text.secondary',
                  transition: 'width 0.2s',
                }}
              />
            </ButtonBase>
          );
        })}
      </Grid>
    </Grid>
  );
};
